import SharedPreferences from "../constants/SharedPreferences";
import {
  aeadDecryptWithAssociatedData,
  aeadEncryptWithAssociatedData,
} from "../crypto/AuthenticatedEncryption";
import { getMemberClientData, saveStatististicsAPI } from "../rest/BayunAPIManager";
import { getFromStorage, saveInStorage } from "../util/Utility";
import { getLMSStatisticsKey } from "./CryptManager";
import { asRawKey } from "../crypto/KeyMaterial";
import { encodeKey } from "../crypto/PBKDF2";

/**
 * Encrypts the member's statistics and calls the API to save them on LMS.
 *
 * @param {String} sessionId sessionId of the session in question.
 * @param {booolean} isDeauthenticateCall boolean that shows if it's a logout call.
 * @param {booolean} isEncryptionUpdate true if encryption was done, else false for decryption.
 */
export const updateAndSaveStatistics = async (
  sessionId,
  isDeauthenticateCall,
  isLoginCall,
  isEncryptionUpdate,
  isGetLockingKeyRequest,
) => {
  let encryptionCount = await getFromStorage(
    sessionId,
    SharedPreferences.ENCRYPTION_COUNT,
  );
  let decryptionCount = await getFromStorage(
    sessionId,
    SharedPreferences.DECRYPTION_COUNT,
  );

  if (!encryptionCount) {
    encryptionCount = 0;
  }

  if (!decryptionCount) {
    decryptionCount = 0;
  }

  const decryptedExpiryTime = await getFromStorage(
    sessionId,
    SharedPreferences.STATS_SYNC_EXPIRY_TIME,
  );

  if (!isDeauthenticateCall && !isLoginCall) {
    if (isGetLockingKeyRequest) {
      encryptionCount = Number(encryptionCount) + 100;
    } else {
      if (isEncryptionUpdate) {
        encryptionCount++;
      } else {
        decryptionCount++;
      }
    }
  }

  // If the current time exceeds the expiry time, save the stats on LMS
  if (new Date().valueOf() >= decryptedExpiryTime || isDeauthenticateCall) {
    if (!isLoginCall) {
      const memberKey = await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_KEY,
      );
      const orgName = await getFromStorage(
        sessionId,
        SharedPreferences.ORG_NAME,
      );
      const orgMemberId = await getFromStorage(
        sessionId,
        SharedPreferences.ORG_MEMBER_ID,
      );
      const applicationId = await getFromStorage(
        sessionId,
        SharedPreferences.APP_ID,
      );

      let statisticsKey = asRawKey(
        await getFromStorage(sessionId, SharedPreferences.STATISTICS_KEY),
      );
      const lmsStatisticsKey = await getLMSStatisticsKey(
        memberKey,
        orgName,
        orgMemberId,
        applicationId,
      );

      // Encrypt the statistics with statisticsKey
      const baseId = orgName + orgMemberId + applicationId;

      if (!statisticsKey) {
        await getMemberClientData(
          sessionId,
          orgMemberId,
          orgName,
          null,
          null
        );
        statisticsKey = asRawKey(
          await getFromStorage(
            sessionId,
            SharedPreferences.STATISTICS_KEY,
          ),
        );
      }

      const encryptedEncryptionCount = await aeadEncryptWithAssociatedData(
        `${encryptionCount}`,
        baseId,
        statisticsKey,
      );

      const encryptedDecryptionCount = await aeadEncryptWithAssociatedData(
        `${decryptionCount}`,
        baseId,
        statisticsKey,
      );

      const statisticsLastOperationTime = new Date().valueOf();
      const encryptedStatisticsLastOperationTime =
        await aeadEncryptWithAssociatedData(
          `${statisticsLastOperationTime}`,
          baseId,
          statisticsKey,
        );

      await saveStatististicsAPI(
        sessionId,
        encryptedDecryptionCount,
        encryptedEncryptionCount,
        encryptedStatisticsLastOperationTime,
        encodeKey(lmsStatisticsKey),
      );

      // What was just sent is now recorded on the server, so the local tally starts again from
      // zero. The server ADDS what it receives to the stored total
      // (MemberStatisticsService.saveOrUpdateMemberStatistics does
      // `old + submitted`), which makes this a delta, not a running total - the parameter is even
      // documented as "times encryption was performed since last time stats were updated".
      //
      // Without the reset every sync window resent everything counted since login and the server
      // added it again: 10 operations then 5 more reported 10, then 25, then 45 - growing with
      // the square of the activity rather than matching it.
      encryptionCount = 0;
      decryptionCount = 0;
    }
  }

  if (isDeauthenticateCall || isLoginCall) {
    await saveInStorage(sessionId, SharedPreferences.ENCRYPTION_COUNT, 0);
    await saveInStorage(sessionId, SharedPreferences.DECRYPTION_COUNT, 0);
  } else {
    await saveInStorage(
      sessionId,
      SharedPreferences.ENCRYPTION_COUNT,
      encryptionCount,
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.DECRYPTION_COUNT,
      decryptionCount,
    );
  }
};
